import {
  buildBlock,
  type BlockTemplate,
  type BuildBlockInput,
  type BuiltBlock,
  type SessionTemplate,
  type WeekParameters,
} from './block-builder.js';
import { TORSO_LEG_SESSIONS } from './splits.js';

/**
 * Plantilla del BLOQUE DE READAPTACION (docs/TRAINING_ENGINE.md §3).
 *
 * Es deterministica y conservadora a proposito: el usuario es avanzado (14
 * años) pero lleva ~6 meses inactivo, asi que se reintroduce carga con RIR alto,
 * sin fallo y sin tecnicas avanzadas.
 *
 * Este modulo ya solo contiene los DATOS de la readaptacion. La maquinaria vive
 * en `block-builder.ts` y las plantillas de sesion en `splits.ts`; aqui no se
 * reexporta ninguna de las dos para que `index.ts` no tenga dos rutas al mismo
 * nombre.
 */

export const READAPTATION_WEEKS = 4;

/**
 * Tabla EXPLICITA del §3, no una rampa generada.
 *
 * El documento fija estos valores semana a semana y no son una interpolacion:
 * las series principales suben 3-3-4-4 y las accesorias 2-3-3-3, que ninguna
 * formula lineal produce a la vez. Generarlos obligaria a elegir un redondeo
 * distinto para cada fila, que es peor que escribirlos.
 */
export const READAPTATION_WEEKS_PARAMS: WeekParameters[] = [
  {
    weekIndex: 1,
    intent: 'reintroduction',
    mainSets: 3,
    accessorySets: 2,
    mainTarget: { repsMin: 8, repsMax: 10, rir: 4 },
    accessoryTarget: { repsMin: 10, repsMax: 12, rir: 4 },
  },
  {
    weekIndex: 2,
    intent: 'reintroduction',
    mainSets: 3,
    accessorySets: 3,
    mainTarget: { repsMin: 8, repsMax: 10, rir: 3.5 },
    accessoryTarget: { repsMin: 10, repsMax: 12, rir: 3.5 },
  },
  {
    weekIndex: 3,
    intent: 'accumulation',
    mainSets: 4,
    accessorySets: 3,
    mainTarget: { repsMin: 8, repsMax: 10, rir: 3 },
    accessoryTarget: { repsMin: 10, repsMax: 12, rir: 3 },
  },
  {
    weekIndex: 4,
    intent: 'accumulation',
    mainSets: 4,
    accessorySets: 3,
    mainTarget: { repsMin: 6, repsMax: 10, rir: 2.5 },
    accessoryTarget: { repsMin: 10, repsMax: 12, rir: 2.5 },
  },
];

/**
 * Torso/Pierna con los objetivos reescritos en clave de reintroduccion. Los
 * huecos son los del split; lo que cambia es como se le cuenta al usuario.
 */
const OBJETIVOS_DE_SESION: Record<string, string> = {
  'Torso A': 'Reintroducir empuje horizontal y tracción vertical',
  'Pierna A': 'Reintroducir sentadilla y bisagra con carga axial contenida',
  'Torso B': 'Reintroducir empuje vertical y tracción horizontal',
  'Pierna B': 'Reintroducir trabajo unilateral y cadena posterior',
};

export const READAPTATION_SESSIONS: SessionTemplate[] = TORSO_LEG_SESSIONS.map((session) => ({
  ...session,
  objective: OBJETIVOS_DE_SESION[session.name] ?? session.objective,
}));

export const READAPTATION_OBJECTIVES: readonly string[] = [
  'Reintroducir carga tras la inactividad sin acumular fatiga innecesaria',
  'Recuperar patrones técnicos con RIR alto y sin fallo',
  'Establecer historial de cargas para el motor de recomendación',
];

export const READAPTATION_EXIT_METRICS: readonly string[] = [
  'RIR real dentro de ±1 del programado en ≥80 % de las series',
  'Agujetas medias ≤3/5',
  'Sin sesiones perdidas por molestias',
];

export const READAPTATION_TEMPLATE: BlockTemplate = {
  name: 'Readaptación',
  phase: 'readaptation',
  split: 'torso_leg',
  maxSessionMinutes: 90,
  objectives: READAPTATION_OBJECTIVES,
  exitMetrics: READAPTATION_EXIT_METRICS,
  weekParams: READAPTATION_WEEKS_PARAMS,
  sessions: READAPTATION_SESSIONS,
};

export function buildReadaptationBlock(input: BuildBlockInput): BuiltBlock {
  return buildBlock(READAPTATION_TEMPLATE, input);
}
